import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { stageRig } from '../lighting/stage-light-rig.js';

// Shared-rig contract (see the framing addendum): this visualizer's
// declared relationship to the one shared StageLightRig instance, and the
// beam intensity it asks for. Most restrictive wins across owners.
const RIG = 'required';
const RIG_OWNER = 'neonparticles';
const RIG_INTENSITY_SCALE = 1;
const RIG_ZONES = 'default';
// No floor here, deliberately: this visualizer's camera sits AT the rig's
// own centre looking outward down the tunnel, so the rig's floor pools
// (which cluster within RIG_RADIUS of the origin) fall behind the camera.
// A ground plane would cost fill-rate and never be seen - verified on a
// real render before removing it again.
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'neonparticles',
  family: 'particles',
  energyLevel: 0.75,
  bpmMin: 100,
  bpmMax: 170,
  movement: 0.55,
  danceability: 0.80,
  tonality: 0.80,
  enabled: true
};

// NeonParticles: a field of glowing neon dots, varying in size, drifting
// slowly downward while sweeping continuously toward the camera - the
// classic "flying through a field of stars/embers" tunnel look.
//
// The camera itself is never moved per-frame: scene-manager.js owns it
// and recomputes position/orientation every frame from a fixed base
// captured once right after init() (see disco-squares.js's own comment
// on this same constraint) - anything this class set on the camera in
// update() would just be overwritten immediately after. The "continuous
// forward flow" illusion is produced the same way disco-squares' grid
// glide is: by moving the CONTENT toward a fixed camera instead, which
// reads identically to a moving camera through a static field.
//
// Particle position/size/color/shimmer-phase are generated once per
// particle (into typed arrays), same convention as spot-wall.js/dust-
// layer.js - only the parts that actually need to change every frame
// (world position via depth advance + fall + sway) are recomputed in
// update(); everything else stays a fixed per-particle identity.

const PARTICLE_COUNT = 450;
// Particles travel along Z from DEPTH_FAR (freshly spawned, far away) to
// DEPTH_NEAR (just past the camera), then respawn back at DEPTH_FAR - a
// simple conveyor rather than true infinite parallax, cheap and
// invisible in practice since the fade in/out (see the vertex shader)
// hides both ends.
const DEPTH_FAR = -45;
const DEPTH_NEAR = 1;
// Particles are sampled on a disk (not a box) so the field reads as a
// tunnel someone is flying through, not a floating cloud - the "falling"
// motion below then makes each particle drift up and down through the
// full disk band around its own sampled position.
const SPAWN_RADIUS = 7.5;
const FIELD_CENTER_Y = 2;
const FALL_SPEED = 0.6; // world units/s - "softly fall"
const SWAY_AMOUNT = 0.4;
const SWAY_SPEED = 0.3;
const SIZE_MIN = 0.06;
const SIZE_MAX = 0.24; // "luminous dots of varying sizes"
const BASE_DIM = 0.03;
const INTENSITY_BOOST = 1.4;
// Per-particle shimmer - each particle has its own random phase/speed
// (aPhase/aShimmerSpeed), not a shared uniform pulse, so it reads as
// organic twinkling across the field rather than a synchronized flash -
// same lesson disco-squares learned the hard way about uniform-driven
// brightness on a THREE.Points material.
const SHIMMER_AMOUNT = 0.35;
const SHIMMER_SPEED_MIN = 1.5;
const SHIMMER_SPEED_MAX = 3.5;
// A gentle, ambient beat-locked brighten across the whole field (see
// beat-pulse.js) - unlike disco-squares' flat grid (where a uniform
// flash reads as a jarring whole-grid strobe), a scattered field of
// already-shimmering dots reads as glitter catching the beat instead;
// the same idea dust-layer already uses for its own soft twinkle.
const BEAT_PULSE_AMOUNT = 0.3;
const BEAT_PULSE_DECAY = 9;
// BPM scales how fast particles travel toward the camera - a faster
// track feels like accelerating through the field, a slower one like
// drifting through it. Clamped so a very slow/fast track doesn't stall
// or blur the motion (same convention as disco-squares' field-rate
// scaling).
const REFERENCE_BPM = 120;
const TEMPO_MULTIPLIER_MIN = 0.6;
const TEMPO_MULTIPLIER_MAX = 1.8;
const FORWARD_SPEED_BASE = 7; // world units/s at REFERENCE_BPM

// Five fixed neon anchors (cyan, magenta, green, purple, gold) rather
// than a continuous band - "vibrant neon shades" reads as a handful of
// distinct saturated colors, not a gradient. Brighter/more saturated
// than disco-squares' warmer pastel-ish palette, since these are meant
// to read as pure glow, not flat tile fills.
const NEON_HUE_ANCHORS = [0.5, 0.83, 0.33, 0.75, 0.13];
const HUE_JITTER = 0.03;
const SAT_MIN = 0.75;
const SAT_MAX = 1.0;
const LIGHT_MIN = 0.55;
const LIGHT_MAX = 0.75;
// Unlike spot-wall.js/disco-squares.js's 50/50 fixed-palette/album-color
// split, every particle here aligns to the album's own dominant color
// when one is available - only its saturation/lightness ("ton") vary
// per particle, not its hue, so the whole field reads as one cohesive
// family of shades tied to the cover rather than a mix of unrelated
// hues. Falls back to the fixed NEON_HUE_ANCHORS palette (unchanged)
// until a palette resolves, or for a track with no artwork at all.
const ALBUM_HUE_JITTER = 0.02; // barely any hue drift - stays recognizably the album's color
const ALBUM_SAT_MIN = 0.55;
const ALBUM_SAT_MAX = 1.0;
const ALBUM_LIGHT_MIN = 0.35;
const ALBUM_LIGHT_MAX = 0.75;

// Beat-synced size pulsation, shared by every particle at once via one
// uniform (uPulseScale) - unlike disco-squares' flat, evenly-lit grid
// (where a uniform brightness flash reads as an obvious whole-grid
// strobe), a field of already depth-scattered, already-shimmering dots
// swelling together on the beat reads as the whole field "breathing"/
// pulsating rather than a mechanical flash - the same reasoning that
// already justifies BEAT_PULSE_AMOUNT's uIntensity pulse above, just
// applied to size instead of brightness, and considerably stronger
// since this is meant to read as a deliberate pulsation, not an
// ambient twinkle.
const PULSE_AMOUNT = 0.6; // fractional size growth right on the beat
const PULSE_DECAY = 10;


const vertexShader = `
  uniform float uTime;
  uniform float uSizeScale;
  uniform float uPulseScale;
  attribute float aSize;
  attribute vec3 aColor;
  attribute float aPhase;
  attribute float aShimmerSpeed;
  varying float vBrightness;
  varying vec3 vColor;

  void main() {
    vColor = aColor;

    // Soft fade-in as a particle spawns far away, fade-out just before
    // it recycles past the camera - keeps both ends of the conveyor
    // invisible instead of a hard pop-in/pop-out at the depth bounds.
    float depthT = clamp((position.z - (${DEPTH_FAR.toFixed(3)})) / ${(DEPTH_NEAR - DEPTH_FAR).toFixed(3)}, 0.0, 1.0);
    float fadeIn = smoothstep(0.0, 0.12, depthT);
    float fadeOut = 1.0 - smoothstep(0.94, 1.0, depthT);

    float shimmer = ${(1 - SHIMMER_AMOUNT).toFixed(3)} + ${SHIMMER_AMOUNT.toFixed(3)} * sin(uTime * aShimmerSpeed + aPhase);
    vBrightness = fadeIn * fadeOut * shimmer;

    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    // Perspective size attenuation (1/-mvPosition.z) is what sells the
    // "flying toward camera" depth cue on its own - particles naturally
    // grow as they approach. uPulseScale layers a shared beat-synced
    // swell on top (see PULSE_AMOUNT above).
    gl_PointSize = aSize * uPulseScale * uSizeScale / -mvPosition.z;
    gl_Position = projectionMatrix * mvPosition;
  }
`;

const fragmentShader = `
  uniform float uBaseDim;
  uniform float uIntensity;
  varying float vBrightness;
  varying vec3 vColor;

  void main() {
    vec2 uv = gl_PointCoord - vec2(0.5);
    float d = length(uv) * 2.0;
    if (d > 1.0) discard;
    float edge = pow(1.0 - d, 1.8);

    float brightness = uBaseDim + vBrightness * uIntensity;
    gl_FragColor = vec4(vColor * brightness, edge * (uBaseDim * 0.5 + vBrightness));
  }
`;

export class NeonParticlesVisualizer extends Visualizer {
  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;

    camera.position.set(0, FIELD_CENTER_Y, 0);
    camera.lookAt(0, FIELD_CENTER_Y, DEPTH_FAR);

    // Same StageLightRig setup as neon-pulse.js - plain, unoffset,
    // built around the world origin.
    this._rig = stageRig.acquire(RIG_OWNER, {
      scene: scene,
      intensityScale: RIG_INTENSITY_SCALE,
      zones: RIG_ZONES
    });

    const count = PARTICLE_COUNT;
    this._count = count;
    const positions = new Float32Array(count * 3);
    const sizes = new Float32Array(count);
    const colors = new Float32Array(count * 3);
    const phases = new Float32Array(count);
    const shimmerSpeeds = new Float32Array(count);
    // Per-particle motion state, mutated every frame in update() - kept
    // as plain arrays (not geometry attributes) since only the derived
    // world position needs to reach the GPU.
    this._swayCenterX = new Float32Array(count);
    this._swayCenterY = new Float32Array(count);
    this._swaySeed = new Float32Array(count);
    this._fallOffset = new Float32Array(count);
    this._z = new Float32Array(count);
    // Fixed per-particle color identity - see _recomputeColors.
    this._baseHue = new Float32Array(count);
    this._baseSat = new Float32Array(count);
    this._baseLight = new Float32Array(count);
    this._jitterSeed = new Float32Array(count); // fallback hue jitter + album-color saturation tone
    this._toneSeed = new Float32Array(count); // album-color lightness tone
    const tmpColor = new THREE.Color();

    for (let i = 0; i < count; i++) {
      this._spawn(i, true);
      sizes[i] = SIZE_MIN + Math.random() * (SIZE_MAX - SIZE_MIN);
      phases[i] = Math.random() * Math.PI * 2;
      shimmerSpeeds[i] = SHIMMER_SPEED_MIN + Math.random() * (SHIMMER_SPEED_MAX - SHIMMER_SPEED_MIN);

      const anchor = NEON_HUE_ANCHORS[Math.floor(Math.random() * NEON_HUE_ANCHORS.length)];
      const hue = (anchor + (Math.random() * 2 - 1) * HUE_JITTER + 1) % 1;
      const sat = SAT_MIN + Math.random() * (SAT_MAX - SAT_MIN);
      const light = LIGHT_MIN + Math.random() * (LIGHT_MAX - LIGHT_MIN);
      this._baseHue[i] = hue;
      this._baseSat[i] = sat;
      this._baseLight[i] = light;
      this._jitterSeed[i] = Math.random();
      this._toneSeed[i] = Math.random();
      tmpColor.setHSL(hue, sat, light);
      colors[i * 3] = tmpColor.r;
      colors[i * 3 + 1] = tmpColor.g;
      colors[i * 3 + 2] = tmpColor.b;

      positions[i * 3] = this._swayCenterX[i];
      positions[i * 3 + 1] = this._swayCenterY[i] + SPAWN_RADIUS - this._fallOffset[i];
      positions[i * 3 + 2] = this._z[i];
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('aSize', new THREE.BufferAttribute(sizes, 1));
    geometry.setAttribute('aColor', new THREE.BufferAttribute(colors, 3));
    geometry.setAttribute('aPhase', new THREE.BufferAttribute(phases, 1));
    geometry.setAttribute('aShimmerSpeed', new THREE.BufferAttribute(shimmerSpeeds, 1));

    this._material = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uSizeScale: { value: 1 },
        uBaseDim: { value: BASE_DIM },
        uIntensity: { value: INTENSITY_BOOST },
        uPulseScale: { value: 1 }
      },
      vertexShader: vertexShader,
      fragmentShader: fragmentShader,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending
    });

    this._points = new THREE.Points(geometry, this._material);
    scene.add(this._points);

    this._time = 0;
    this._lastPaletteRef = null;
  }

  // (Re)seeds particle i's disk position/fall-phase. `initial` staggers
  // the starting depth/fall-offset across the whole field (called once
  // per particle at init) so it doesn't visibly "reset" in unison - a
  // later in-flight respawn (initial:false) always restarts at the far
  // plane, with a small random depth jitter so recycled particles don't
  // all pop in at the exact same z either.
  _spawn(i, initial) {
    const angle = Math.random() * Math.PI * 2;
    const radius = Math.sqrt(Math.random()) * SPAWN_RADIUS;
    this._swayCenterX[i] = Math.cos(angle) * radius;
    this._swayCenterY[i] = FIELD_CENTER_Y + Math.sin(angle) * radius;
    this._swaySeed[i] = Math.random() * Math.PI * 2;
    this._fallOffset[i] = initial ? Math.random() * SPAWN_RADIUS * 2 : this._fallOffset[i];
    this._z[i] = initial ? DEPTH_FAR + Math.random() * (DEPTH_NEAR - DEPTH_FAR) : DEPTH_FAR + Math.random() * 2;
  }

  // Every particle takes a tonal variation of the album's own dominant
  // color when one is available - same hue (only barely jittered), but
  // spread across a range of saturation/lightness so the field reads as
  // many shades of the cover's color rather than flat repetition or a
  // mix of unrelated hues. Falls back to the fixed multi-hue neon
  // palette (assigned once per particle at init - see this._baseHue/
  // _baseSat/_baseLight) until a palette resolves, or for a track with
  // no artwork at all.
  _recomputeColors(profile) {
    const dominant = profile.artworkPalette && profile.artworkPalette.dominantColors;
    const hasDominant = dominant && dominant.length > 0;
    const tmpColor = new THREE.Color();
    const hsl = { h: 0, s: 0, l: 0 };
    if (hasDominant) {
      tmpColor.set(dominant[0]);
      tmpColor.getHSL(hsl);
    }
    const colorAttr = this._points.geometry.attributes.aColor;
    for (let i = 0; i < this._count; i++) {
      if (hasDominant) {
        const hue = (hsl.h + (this._jitterSeed[i] - 0.5) * 2 * ALBUM_HUE_JITTER + 1) % 1;
        const sat = ALBUM_SAT_MIN + this._jitterSeed[i] * (ALBUM_SAT_MAX - ALBUM_SAT_MIN);
        const light = ALBUM_LIGHT_MIN + this._toneSeed[i] * (ALBUM_LIGHT_MAX - ALBUM_LIGHT_MIN);
        tmpColor.setHSL(hue, sat, light);
      } else {
        tmpColor.setHSL(this._baseHue[i], this._baseSat[i], this._baseLight[i]);
      }
      colorAttr.array[i * 3] = tmpColor.r;
      colorAttr.array[i * 3 + 1] = tmpColor.g;
      colorAttr.array[i * 3 + 2] = tmpColor.b;
    }
    colorAttr.needsUpdate = true;
  }

  update(deltaTime, profile) {
    stageRig.update(deltaTime, profile, RIG_OWNER);

    const paused = profile.playbackState !== 'PLAYING';
    const bpm = profile.trackFeatures && profile.trackFeatures.bpm;
    const tempoMul = bpm
      ? THREE.MathUtils.clamp(bpm / REFERENCE_BPM, TEMPO_MULTIPLIER_MIN, TEMPO_MULTIPLIER_MAX)
      : 1;
    if (!paused) this._time += deltaTime;
    this._material.uniforms.uTime.value = this._time;

    if (!paused) {
      const forwardStep = FORWARD_SPEED_BASE * tempoMul * deltaTime;
      const fallStep = FALL_SPEED * deltaTime;
      const position = this._points.geometry.attributes.position;
      for (let i = 0; i < this._count; i++) {
        this._z[i] += forwardStep;
        if (this._z[i] > DEPTH_NEAR) this._spawn(i, false);

        // Falls through a band of height 2*SPAWN_RADIUS centered on the
        // particle's own sampled disk position, wrapping back to the
        // top once it reaches the bottom - continuous "snow" motion
        // layered on top of the forward conveyor.
        this._fallOffset[i] = (this._fallOffset[i] + fallStep) % (SPAWN_RADIUS * 2);
        const sway = Math.sin(this._time * SWAY_SPEED + this._swaySeed[i]) * SWAY_AMOUNT;

        position.array[i * 3] = this._swayCenterX[i] + sway;
        position.array[i * 3 + 1] = this._swayCenterY[i] + SPAWN_RADIUS - this._fallOffset[i];
        position.array[i * 3 + 2] = this._z[i];
      }
      position.needsUpdate = true;
    }

    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      this._recomputeColors(profile);
    }

    const canvasPixelHeight = window.innerHeight * Math.min(window.devicePixelRatio || 1, 2);
    const fovRadians = THREE.MathUtils.degToRad(this._camera.fov);
    this._material.uniforms.uSizeScale.value = canvasPixelHeight / (2 * Math.tan(fovRadians / 2));

    const intensity = profile.director.intensity;
    const pulse = profile.director.pulse(BEAT_PULSE_AMOUNT, BEAT_PULSE_DECAY);
    this._material.uniforms.uIntensity.value = INTENSITY_BOOST * (0.75 + intensity * 0.5) * pulse;
    this._material.uniforms.uPulseScale.value = profile.director.pulse(PULSE_AMOUNT, PULSE_DECAY);
  }

  getLightRig() {
    return this._rig;
  }

  dispose() {
    stageRig.release(RIG_OWNER);
    this._rig = null;
    this._scene.remove(this._points);
    this._points.geometry.dispose();
    this._material.dispose();
  }
}
